import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';

/**
 * The app's language registry (Rule 56).
 *
 * English is bundled because it is the default AND the fallback — a missing key
 * in any other language has to resolve to something. Every other locale is a
 * separate lazy chunk fetched the first time someone selects it, so adding the
 * tenth language costs the initial download nothing.
 *
 * Adding a language is three steps and no refactor: add an entry here, create
 * `locales/{code}/common.json` at full key parity with `en`, done — the
 * `<LanguageSwitch>` picks it up automatically.
 */
export const LOCALES = [
  { code: 'en', label: 'English', load: null },
  { code: 'tl', label: 'Filipino', load: () => import('./locales/tl/common.json') },
];

const STORAGE_KEY = 'app_locale';

const isSupported = (code) => LOCALES.some((l) => l.code === code);

const initialLocale = (() => {
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && isSupported(saved)) return saved;

  const fromEnv = process.env.REACT_APP_I18N_DEFAULT_LOCALE;
  return fromEnv && isSupported(fromEnv) ? fromEnv : 'en';
})();

i18n.use(initReactI18next).init({
  resources: { en: { common: enCommon } },
  lng: 'en',                                  // real locale applied by setLocale below
  fallbackLng: 'en',
  supportedLngs: LOCALES.map((l) => l.code),
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  // No root <Suspense> in this app, so i18next must not suspend on a lazy
  // locale — setLocale() awaits the chunk before switching instead.
  react: { useSuspense: false },
});

/**
 * Switches language, fetching the locale chunk first so the UI never flashes
 * through English on the way. Silently keeps the current language if the chunk
 * fails to load (offline, bad deploy) — a failed download must not blank the app.
 */
export async function setLocale(code) {
  const entry = LOCALES.find((l) => l.code === code);
  if (!entry) return;

  if (entry.load && !i18n.hasResourceBundle(code, 'common')) {
    try {
      const mod = await entry.load();
      i18n.addResourceBundle(code, 'common', mod.default ?? mod, true, true);
    } catch {
      return;
    }
  }

  await i18n.changeLanguage(code);
  window.localStorage.setItem(STORAGE_KEY, code);
  document.documentElement.lang = code;
}

document.documentElement.lang = initialLocale;
if (initialLocale !== 'en') setLocale(initialLocale);

export default i18n;
